import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Grid,
  Box,
  Divider,
  Stack,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
} from "@mui/material";
import { MdClose, MdInfo } from "react-icons/md";
import axios from "../../plugins/axios";
import { getTerminalDetails } from "../../plugins/urls";
import moment from "moment";

const PosDeviceDetails = ({ open, onClose, terminalId }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (open && terminalId) {
      fetchDetails();
    }
  }, [open, terminalId]);

  const fetchDetails = () => {
    setLoading(true);
    axios
      .get(getTerminalDetails(terminalId))
      .then((res) => {
        // Based on the provided request sample structure
        const data = res.data.respBody || res.data.data;
        setDetails(data);
      })
      .catch((err) => {
        console.error("Failed to fetch terminal details", err);
      })
      .finally(() => setLoading(false));
  };

  const DetailItem = ({ label, value, color }) => {
    // If value is an object, try to find a displayable string property
    const displayValue = (typeof value === 'object' && value !== null && !React.isValidElement(value))
      ? (value.terminalName || value.name || JSON.stringify(value))
      : value;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
          {displayValue || "N/A"}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MdInfo color={theme.palette.primary.main} />
          <Typography variant="h6" fontWeight={800}>Terminal Details</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <MdClose />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : details ? (
          <Grid container spacing={3}>
            {/* Basic Info Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 2, textTransform: 'uppercase' }}>
                Hardware Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Terminal ID" value={details.terminal?.terminalId} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Serial Number" value={details.terminal?.terminalSerialNumber} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Terminal Type" value={details.terminal?.terminalType || details.terminalType} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Actual Name" value={details.terminal?.actualTerminalName} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="AccountNumber" value={details.terminal?.posAccountNumber} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Location" value={details.merchant?.officeAddress || details.terminal?.terminalState || details.terminalState} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Status" value={
                    <Chip 
                      label={details.status || details.terminal?.status} 
                      size="small"
                      color={(details.status || details.terminal?.status) === "ACTIVE" ? "success" : "default"}
                      sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                    />
                  } />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* Merchant Info Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 2, textTransform: 'uppercase' }}>
                Assignment Information
              </Typography>
              {details.merchant ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <DetailItem label="Business Name" value={details.merchant.orgName} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailItem label="Merchant Owner" value={`${details.merchant.firstname || ""} ${details.merchant.surname || ""}`} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailItem label="Merchant ID" value={details.merchant.merchantId} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailItem label="Contact Phone" value={details.merchant.phoneNumber} />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  This terminal is currently not assigned to any merchant.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* Usage Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 2, textTransform: 'uppercase' }}>
                Activity & Logs
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <DetailItem 
                    label="Last Used" 
                    value={
                      (details.lastUsedAt || details.terminal?.lastUsedAt || details.issuedDate) 
                        ? moment(details.lastUsedAt || details.terminal?.lastUsedAt || details.issuedDate).format("LLL") 
                        : "Never Used"
                    } 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Date Created" value={moment(details.dateCreated).format("LLL")} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailItem label="Date Updated" value={moment(details.dateUpdated).format("LLL")} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">No details found for this terminal.</Typography>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
        <Button 
          variant="contained" 
          onClick={onClose} 
          sx={{ textTransform: 'none', fontWeight: 600, px: 4, borderRadius: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PosDeviceDetails;
